import { createContext, useContext, useEffect, useId, useLayoutEffect } from 'react';

// Settings save (settings-save): settings keep the person's edits as a draft until they press Save.
// While a draft is unsaved, leaving asks first: closing or reloading the tab through the browser's
// own beforeunload prompt (here), and a link or Back inside the app through UnsavedChangesGuard
// (components/ui/unsaved-changes.tsx), which this registers with.

export type RegisterUnsaved = (id: string, dirty: boolean) => void;
export const UnsavedContext = createContext<RegisterUnsaved | null>(null);

/**
 * `useUnsavedChanges(dirty)`: while `dirty`, closing or reloading the tab asks first, and so does
 * navigating away inside the app (under UnsavedChangesGuard). SettingsSaveBar calls it for you.
 */
export function useUnsavedChanges(dirty: boolean) {
  const register = useContext(UnsavedContext);
  const id = useId();
  // At commit (a layout effect), so the guard knows as soon as the page shows the change.
  useLayoutEffect(() => {
    register?.(id, dirty);
    return () => register?.(id, false);
  }, [register, id, dirty]);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers show the prompt only when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);
}
